import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Lock, Shield, ChevronLeft, Save, Download, Upload, RefreshCw, 
  Eye, EyeOff, BookOpen, PenTool, Layout, FileText, Mic, Clock,
  Calendar, Star, Plus, ArrowLeft, ArrowRight, Moon, Sun, Settings,
  MoreVertical, Trash, Tag, Share
} from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import BottomNavigation from '../components/BottomNavigation';
import EndlessNotesCanvas from '../components/EndlessNotesCanvas';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { STORAGE_KEYS, ENDLESS_NOTES } from '../utils/constants';

/**
 * Dijital Kasa / Hafıza Sarayı Ekranı
 * Kullanıcının not ve fikirlerini zihin sarayı formatında oluşturmasını, düzenlemesini
 * ve blockchain üzerinde saklamasını sağlar
 * 
 * Gelişmiş AI asistanı ile günlük tutma ve ses kaydından not çıkarma özellikleri içerir
 * Arweave ve Polygon blockchain entegrasyonları bulunur
 */
const DigitalVaultScreen = () => {
  const { navigateToScreen, showSecurityVerification } = useUI();
  const { user, isAuthenticated } = useAuth();
  
  // Durum değişkenleri
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [verificationStep, setVerificationStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [vaultPassword, setVaultPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [securityQuestion] = useState("İlk evcil hayvanınızın adı nedir?");
  const [isAuthenticated2FA, setIsAuthenticated2FA] = useState(false);
  const [isBlockchainSaving, setIsBlockchainSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Yeni Eklenmiş Durum Değişkenleri
  const [activeTab, setActiveTab] = useState('canvas'); // 'canvas', 'journal', 'settings'
  const [isJournalOpen, setIsJournalOpen] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [currentJournal, setCurrentJournal] = useState('');
  const [aiJournalPrompt, setAiJournalPrompt] = useState('');
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [blockchainNetwork, setBlockchainNetwork] = useState('arweave'); // 'arweave' veya 'polygon'
  const [aiSetting, setAiSetting] = useState('active');
  const [saveFrequency, setSaveFrequency] = useState(30); // dakika cinsinden
  const [toolbarPosition, setToolbarPosition] = useState('bottom'); // 'top', 'bottom', 'left', 'right'
  const [currentNodeType, setCurrentNodeType] = useState('thought');
  const [isAiAssistantListening, setIsAiAssistantListening] = useState(false);
  
  // Canvas'a referans
  const canvasRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  useEffect(() => {
    // Kimlik doğrulaması yapılmamışsa giriş ekranına yönlendir
    if (!isAuthenticated) {
      navigateToScreen('login');
      return;
    }
    
    // Demo amaçlı yükleme ekranı
    const timer = setTimeout(() => {
      setIsLoading(false);
      setIsAuthenticating(true);
    }, 1500);
    
    // Önceden kaydedilmiş günlük girişlerini yükle
    loadJournalEntries();
    
    // Otomatik kaydetme zamanlayıcısını başlat
    startAutoSaveTimer();
    
    return () => {
      clearTimeout(timer);
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [isAuthenticated, navigateToScreen]);

  // Otomatik kaydetme zamanlayıcısını başlat
  const startAutoSaveTimer = () => {
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }
    
    // Her {saveFrequency} dakikada bir kaydet
    autoSaveTimerRef.current = setInterval(() => {
      if (canvasRef.current && isAuthenticated2FA) {
        saveToBlockchain(true); // sessiz kaydetme
      }
    }, saveFrequency * 60 * 1000);
  };

  // Kayıt sıklığı değiştiğinde zamanlayıcıyı güncelle
  useEffect(() => {
    startAutoSaveTimer();
  }, [saveFrequency]);

  // Dark mode değiştiğinde uygulanması
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
  }, [darkMode]);

  // Günlük girişlerini yükle
  const loadJournalEntries = useCallback(async () => {
    try {
      const entries = await getFromStorage(STORAGE_KEYS.ENDLESS_NOTES_JOURNAL, []);
      if (entries && Array.isArray(entries)) {
        setJournalEntries(entries);
      }
    } catch (error) {
      console.error("Günlük girişleri yüklenirken hata:", error);
    }
  }, []);

  // Günlük girişlerini kaydet
  const saveJournalEntry = useCallback(async () => {
    if (!currentJournal.trim()) return;
    
    const newEntry = {
      id: Date.now(),
      date: new Date().toISOString(),
      content: currentJournal,
      aiGenerated: isAiGenerating,
      tags: extractTags(currentJournal)
    };
    
    const updatedEntries = [newEntry, ...journalEntries];
    setJournalEntries(updatedEntries);
    setCurrentJournal('');
    
    try {
      await saveToStorage(STORAGE_KEYS.ENDLESS_NOTES_JOURNAL, updatedEntries);
    } catch (error) {
      console.error("Günlük girişi kaydedilirken hata:", error);
    }
  }, [currentJournal, journalEntries, isAiGenerating]);

  // Metinden etiketleri çıkar (#etiket formatında)
  const extractTags = (text) => {
    const tags = [];
    const tagRegex = /#(\w+)/g;
    let match;
    
    while ((match = tagRegex.exec(text)) !== null) {
      tags.push(match[1]);
    }
    
    return tags;
  };

  // AI ile günlük girişi oluştur
  const generateAiJournal = useCallback(async () => {
    if (!aiJournalPrompt.trim()) return;
    
    setIsAiGenerating(true);
    
    try {
      // Gerçek uygulamada burada bir AI API'sine istek atılır
      // Şimdilik demo yanıt oluşturuyoruz
      
      setTimeout(() => {
        const aiResponse = `Bugün ${new Date().toLocaleDateString('tr-TR')} tarihinde, "${aiJournalPrompt}" konusu üzerine düşüncelerimi paylaşmak istiyorum.

Bu konu beni son zamanlarda oldukça meşgul ediyor. Aslında, hayatımın birçok alanında bu durumun yansımalarını görebiliyorum. Bazen kendime soruyorum: Nasıl daha etkili bir yaklaşım geliştirebilirim?

İşte bazı fikirlerim:
1. Daha fazla araştırma yapmalıyım
2. Konuyla ilgili uzmanlarla görüşmeliyim
3. Pratik uygulamalar deneyerek ilerleme kaydetmeliyim

#kişiselgelişim #motivasyon #öğrenme`;

        setCurrentJournal(aiResponse);
        setIsAiGenerating(false);
      }, 2000);
    } catch (error) {
      console.error("AI günlük oluşturma hatası:", error);
      setIsAiGenerating(false);
    }
  }, [aiJournalPrompt]);

  // Ses kaydını başlat/durdur
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      // Kayıt durdurma ve ses tanıma (demo)
      setIsRecording(false);
      
      setTimeout(() => {
        // Demo için sabit bir metin
        setCurrentJournal(prev => prev + 
          "\n\nSes kaydından metne dönüştürüldü: Bugün yapılacaklar listesi - market alışverişi, fatura ödemeleri ve akşam için yemek hazırlığı."
        );
      }, 1500);
    } else {
      // Kayıt başlatma
      setIsRecording(true);
    }
  }, [isRecording]);

  // AI asistanı dinleme modunu aç/kapat
  const toggleAiAssistant = useCallback(() => {
    setIsAiAssistantListening(prev => !prev);
    
    if (!isAiAssistantListening) {
      // AI asistanı başlat
      alert("AI asistanı artık sizi dinliyor ve günlüğünüze eklemeler yapacak (Demo)");
    } else {
      // AI asistanı durdur
      alert("AI asistanı durduruldu (Demo)");
    }
  }, [isAiAssistantListening]);

  // Güvenlik doğrulama işlemi - Şifre kontrol
  const verifyVaultPassword = () => {
    // Demo için basit bir şifre kontrolü
    const isPasswordCorrect = vaultPassword === "123456";
    
    if (isPasswordCorrect) {
      setVerificationStep(3);
    } else {
      alert("Hatalı şifre, lütfen tekrar deneyin.");
      setVaultPassword('');
    }
  };

  // Güvenlik doğrulama işlemi - Güvenlik sorusu
  const verifySecurityQuestion = () => {
    // Demo için basit güvenlik sorusu kontrolü
    const isAnswerCorrect = securityAnswer.toLowerCase() === "demo";
    
    if (isAnswerCorrect) {
      completeVerification();
    } else {
      alert("Hatalı cevap, lütfen tekrar deneyin.");
      setSecurityAnswer('');
    }
  };

  // Doğrulama tamamlandı
  const completeVerification = () => {
    setIsAuthenticated2FA(true);
    setIsAuthenticating(false);
  };

  // Blockchain'e kaydet
  const saveToBlockchain = async (silent = false) => {
    if (!canvasRef.current) return;
    
    setIsBlockchainSaving(true);
    try {
      // Canvas içeriğini al
      const canvasData = canvasRef.current.getCanvasData();
      
      // Günlük verilerini ekle
      const combinedData = {
        canvas: canvasData,
        journal: journalEntries,
        lastUpdated: new Date().toISOString(),
        network: blockchainNetwork
      };
      
      // Gerçek uygulamada burada Arweave veya Polygon blockchain sistemine kayıt işlemi yapılır
      // Şimdilik yerel depolamaya kaydedelim
      await saveToStorage(STORAGE_KEYS.ENDLESS_NOTES_DATA, combinedData);
      
      // Blockchain kaydı bilgisini sakla
      await saveToStorage(STORAGE_KEYS.ENDLESS_NOTES_BLOCKCHAIN, {
        lastSavedTimestamp: Date.now(),
        network: blockchainNetwork,
        txId: `mock-tx-${Date.now()}`, // Gerçek uygulamada blockchain işlem ID'si
        status: 'confirmed'
      });
      
      setLastSaved(new Date());
      
      if (!silent) {
        alert(`Veriler başarıyla ${blockchainNetwork === 'arweave' ? 'Arweave' : 'Polygon'} blockchain'ine kaydedildi! (Demo)`);
      }
      return true;
    } catch (error) {
      console.error("Blockchain'e kaydetme hatası:", error);
      if (!silent) {
        alert("Kaydetme işlemi sırasında bir hata oluştu.");
      }
      return false;
    } finally {
      setIsBlockchainSaving(false);
    }
  };

  // Verileri dışa aktar
  const exportData = async () => {
    if (!canvasRef.current) return;
    
    try {
      const canvasData = canvasRef.current.getCanvasData();
      const exportData = {
        canvas: canvasData,
        journal: journalEntries,
        exportDate: new Date().toISOString(),
        user: user ? user.username : 'anonymous'
      };
      
      // JSON dosyasını oluştur ve indir
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `hafiza-sarayi-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert("Veriler başarıyla dışa aktarıldı!");
    } catch (error) {
      console.error("Dışa aktarma hatası:", error);
      alert("Dışa aktarma işlemi sırasında bir hata oluştu.");
    }
  };

  // Verileri içe aktar
  const importData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    
    input.onchange = async (e) => {
      try {
        const file = e.target.files[0];
        const reader = new FileReader();
        
        reader.onload = async (event) => {
          try {
            const data = JSON.parse(event.target.result);
            
            if (data.canvas && data.journal) {
              // Canvas verilerini yükle
              if (canvasRef.current) {
                canvasRef.current.loadCanvasData(data.canvas);
              }
              
              // Günlük verilerini yükle
              setJournalEntries(data.journal);
              await saveToStorage(STORAGE_KEYS.ENDLESS_NOTES_JOURNAL, data.journal);
              
              alert("Veriler başarıyla içe aktarıldı!");
            } else {
              alert("Geçersiz dosya formatı. Lütfen geçerli bir Hafıza Sarayı export dosyası seçin.");
            }
          } catch (parseError) {
            console.error("Dosya okuma hatası:", parseError);
            alert("Dosya okuma hatası: Geçersiz JSON formatı.");
          }
        };
        
        reader.readAsText(file);
      } catch (error) {
        console.error("İçe aktarma hatası:", error);
        alert("İçe aktarma işlemi sırasında bir hata oluştu.");
      }
    };
    
    input.click();
  };

  // Yükleme ekranı
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-white items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <BookOpen size={48} className="text-indigo-600 animate-pulse" />
          <h2 className="text-xl font-bold">Hafıza Sarayı Yükleniyor</h2>
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  // Doğrulama ekranları
  if (isAuthenticating) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex flex-col items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
          <div className="text-center mb-6">
            <div className="w-20 h-20 mx-auto bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <Lock size={40} className="text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Hafıza Sarayı</h2>
            <p className="text-gray-600">
              {verificationStep === 1 && "Lütfen kimliğinizi doğrulayın"}
              {verificationStep === 2 && "Lütfen kasa şifrenizi girin"}
              {verificationStep === 3 && "Son güvenlik adımı"}
            </p>
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between mb-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    verificationStep >= step 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  <span className="text-xs mt-1 text-gray-600">
                    {step === 1 && "Kimlik"}
                    {step === 2 && "Şifre"}
                    {step === 3 && "Soru"}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {verificationStep === 1 && (
            <div className="mb-6">
              <button 
                className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium"
                onClick={() => setVerificationStep(2)}
              >
                Parmak İzi ile Doğrula
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Demo için sadece tıklayın
              </p>
            </div>
          )}
          
          {verificationStep === 2 && (
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Kasa Şifresi
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Şifrenizi girin"
                  value={vaultPassword}
                  onChange={(e) => setVaultPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Demo şifre: 123456
              </p>
            </div>
          )}
          
          {verificationStep === 3 && (
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Güvenlik Sorusu
              </label>
              <p className="mb-2 text-gray-700">{securityQuestion}</p>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Cevabınızı girin"
                value={securityAnswer}
                onChange={(e) => setSecurityAnswer(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Demo cevap: demo
              </p>
            </div>
          )}
          
          <div className="flex justify-between">
            <button
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              onClick={() => navigateToScreen('dashboard')}
            >
              İptal
            </button>
            
            {verificationStep === 2 && (
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                onClick={verifyVaultPassword}
                disabled={!vaultPassword}
              >
                Devam
              </button>
            )}
            
            {verificationStep === 3 && (
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                onClick={verifySecurityQuestion}
                disabled={!securityAnswer}
              >
                Kasayı Aç
              </button>
            )}
            
            {verificationStep === 1 && (
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                onClick={() => setVerificationStep(2)}
              >
                Devam
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ana ekran - doğrulama tamamlandıktan sonra
  return (
    <PageLayout>
      {/* Üst Başlık */}
      <header className="bg-white p-4 shadow z-10 sticky top-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigateToScreen('dashboard')}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center">
              <h1 className="font-bold text-lg ml-2">Hafıza Sarayı</h1>
              <Shield size={16} className="ml-2 text-green-600" />
            </div>
          </div>
          
          <div className="flex items-center">
            {isBlockchainSaving ? (
              <div className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-lg flex items-center mr-2">
                <RefreshCw size={16} className="animate-spin mr-1" />
                <span className="text-xs">Kaydediliyor</span>
              </div>
            ) : lastSaved ? (
              <div className="px-2 py-1 bg-green-100 text-green-800 rounded-lg mr-2 text-xs">
                Son kayıt: {lastSaved.toLocaleTimeString()}
              </div>
            ) : null}
            
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('canvas')}
                className={`p-2 rounded-full ${activeTab === 'canvas' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <Layout size={20} />
              </button>
              
              <button
                onClick={() => setActiveTab('journal')}
                className={`p-2 rounded-full ${activeTab === 'journal' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <FileText size={20} />
              </button>
              
              <button
                onClick={() => setActiveTab('settings')}
                className={`p-2 rounded-full ${activeTab === 'settings' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Sekme içerik başlıkları */}
        {activeTab === 'canvas' && (
          <div className="flex justify-between items-center mt-2 px-1">
            <div className="flex space-x-2 overflow-x-auto pb-1">
              {Object.keys(ENDLESS_NOTES.NODE_TYPES).map((type) => (
                <button
                  key={type}
                  className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${
                    currentNodeType === type 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => setCurrentNodeType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="flex">
              <button
                onClick={saveToBlockchain}
                className="p-2 bg-indigo-100 text-indigo-700 rounded-full"
                disabled={isBlockchainSaving}
              >
                <Save size={20} />
              </button>
            </div>
          </div>
        )}
        
        {activeTab === 'journal' && (
          <div className="flex justify-between items-center mt-2">
            <div className="text-sm font-medium text-gray-600">
              {journalEntries.length > 0 ? `${journalEntries.length} Günlük Girişi` : 'Henüz Giriş Yok'}
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={toggleAiAssistant}
                className={`p-2 rounded-full ${
                  isAiAssistantListening 
                    ? 'bg-purple-100 text-purple-700 animate-pulse' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                <Mic size={18} />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Ana İçerik */}
      <main className="flex-1 overflow-hidden">
        {/* Canvas Sekmesi */}
        {activeTab === 'canvas' && (
          <EndlessNotesCanvas 
            ref={canvasRef} 
            currentNodeType={currentNodeType}
            darkMode={darkMode}
          />
        )}
        
        {/* Günlük Sekmesi */}
        {activeTab === 'journal' && (
          <div className="flex flex-col h-full bg-gray-50">
            {/* Günlük Girişleri Listesi */}
            <div className="flex-1 overflow-y-auto p-4">
              {journalEntries.length > 0 ? (
                <div className="space-y-4">
                  {journalEntries.map(entry => (
                    <div 
                      key={entry.id} 
                      className="bg-white rounded-lg shadow p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-xs text-gray-500">
                          {new Date(entry.date).toLocaleDateString('tr-TR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div className="flex space-x-1">
                          {entry.aiGenerated && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              AI
                            </span>
                          )}
                          <button className="text-gray-400 hover:text-gray-600">
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="prose prose-sm max-w-none">
                        {entry.content.split('\n').map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                      
                      {entry.tags && entry.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {entry.tags.map(tag => (
                            <span 
                              key={tag} 
                              className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <FileText size={40} className="mb-2 text-gray-400" />
                  <p>Henüz günlük girişi bulunmuyor</p>
                  <p className="text-sm">Aşağıdan ilk girişinizi ekleyin</p>
                </div>
              )}
            </div>
            
            {/* Yeni Günlük Girişi */}
            <div className="bg-white p-4 border-t border-gray-200">
              <div className="flex mb-2">
                <button 
                  className={`px-3 py-1 rounded-lg text-sm mr-2 ${
                    !isAiGenerating 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => setIsAiGenerating(false)}
                >
                  Manuel
                </button>
                <button 
                  className={`px-3 py-1 rounded-lg text-sm ${
                    isAiGenerating 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => setIsAiGenerating(true)}
                >
                  AI Yardımı
                </button>
              </div>
              
              {isAiGenerating ? (
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="AI'ya bir konu verin..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={aiJournalPrompt}
                    onChange={(e) => setAiJournalPrompt(e.target.value)}
                  />
                  <button
                    className="mt-2 w-full py-2 bg-purple-600 text-white rounded-lg disabled:bg-purple-300"
                    onClick={generateAiJournal}
                    disabled={!aiJournalPrompt.trim()}
                  >
                    AI ile Günlük Oluştur
                  </button>
                </div>
              ) : (
                <div className="relative mb-2">
                  <textarea
                    placeholder="Bugün neler yaşadınız?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px] resize-none"
                    value={currentJournal}
                    onChange={(e) => setCurrentJournal(e.target.value)}
                  />
                  <button
                    className={`absolute bottom-2 right-2 p-2 rounded-full ${
                      isRecording 
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={toggleRecording}
                  >
                    <Mic size={18} />
                  </button>
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:bg-indigo-300"
                  onClick={saveJournalEntry}
                  disabled={!currentJournal.trim()}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Ayarlar Sekmesi */}
        {activeTab === 'settings' && (
          <div className="bg-gray-50 h-full overflow-y-auto p-4">
            <div className="max-w-md mx-auto">
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <h3 className="font-bold text-lg mb-4">Görünüm Ayarları</h3>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    {darkMode ? <Moon size={18} className="mr-2" /> : <Sun size={18} className="mr-2" />}
                    <span>Karanlık Mod</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      checked={darkMode}
                      onChange={() => setDarkMode(!darkMode)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="py-2 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Araç Çubuğu Konumu
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={toolbarPosition}
                    onChange={(e) => setToolbarPosition(e.target.value)}
                  >
                    <option value="top">Üst</option>
                    <option value="bottom">Alt</option>
                    <option value="left">Sol</option>
                    <option value="right">Sağ</option>
                  </select>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <h3 className="font-bold text-lg mb-4">Blockchain Ayarları</h3>
                
                <div className="py-2 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Blockchain Ağı
                  </label>
                  <div className="flex">
                    <button
                      className={`flex-1 py-2 px-3 ${blockchainNetwork === 'arweave' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'} rounded-l-lg`}
                      onClick={() => setBlockchainNetwork('arweave')}
                    >
                      Arweave
                    </button>
                    <button
                      className={`flex-1 py-2 px-3 ${blockchainNetwork === 'polygon' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'} rounded-r-lg`}
                      onClick={() => setBlockchainNetwork('polygon')}
                    >
                      Polygon
                    </button>
                  </div>
                </div>
                
                <div className="py-2 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Otomatik Kayıt Sıklığı
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={saveFrequency}
                    onChange={(e) => setSaveFrequency(parseInt(e.target.value))}
                  >
                    <option value="5">5 dakika</option>
                    <option value="15">15 dakika</option>
                    <option value="30">30 dakika</option>
                    <option value="60">1 saat</option>
                  </select>
                </div>
                
                <div className="py-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yedekleme ve Aktarım
                  </label>
                  <div className="flex space-x-2">
                    <button
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center"
                      onClick={exportData}
                    >
                      <Download size={16} className="mr-1" />
                      Dışa Aktar
                    </button>
                    <button
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg flex items-center justify-center"
                      onClick={importData}
                    >
                      <Upload size={16} className="mr-1" />
                      İçe Aktar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <h3 className="font-bold text-lg mb-4">AI Asistanı Ayarları</h3>
                
                <div className="py-2 border-b border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Asistanı
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={aiSetting}
                    onChange={(e) => setAiSetting(e.target.value)}
                  >
                    <option value="active">Aktif (Her zaman açık)</option>
                    <option value="manual">Manuel (Sadece istendiğinde)</option>
                    <option value="disabled">Devre Dışı</option>
                  </select>
                </div>
                
                <div className="py-2">
                  <button
                    className="w-full py-2 bg-purple-600 text-white rounded-lg flex items-center justify-center"
                    onClick={toggleAiAssistant}
                  >
                    <Mic size={16} className="mr-1" />
                    {isAiAssistantListening ? 'AI Asistanı Durdur' : 'AI Asistanı Başlat'}
                  </button>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    AI asistanı sizi dinler ve günlüğünüze otomatik girişler ekler.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Alt Menü */}
      <BottomNavigation activeItem="vault" />
    </PageLayout>
  );
};

export default DigitalVaultScreen;